import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AccountService, { NotificationPreferences } from '../../../services/account.service';

const PREFS: { key: keyof NotificationPreferences; labelKey: string; descKey: string }[] = [
  { key: 'renewal_reminder', labelKey: 'account.preferences.renewal_reminder', descKey: 'account.preferences.renewal_reminder_desc' },
  { key: 'receipt', labelKey: 'account.preferences.receipt', descKey: 'account.preferences.receipt_desc' },
  { key: 'suspension', labelKey: 'account.preferences.suspension', descKey: 'account.preferences.suspension_desc' },
  { key: 'invite', labelKey: 'account.preferences.invite', descKey: 'account.preferences.invite_desc' },
  { key: 'system', labelKey: 'account.preferences.system', descKey: 'account.preferences.system_desc' },
];

const PreferencesTab: React.FC = () => {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState<NotificationPreferences>({
    renewal_reminder: true,
    receipt: true,
    suspension: true,
    invite: true,
    system: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    AccountService.getPreferences()
      .then((res: any) => setPrefs(res.data ?? {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key: keyof NotificationPreferences) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await AccountService.updatePreferences(prefs);
      toast.success(t('account.preferences.saved') as string);
    } catch {
      toast.error(t('account.preferences.saveError') as string);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border spinner-border-sm text-primary" />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-4">
      <div className="card" style={{ borderRadius: '0.75rem' }}>
        <div className="card-header border-bottom pb-3">
          <h6 className="mb-0 fw-semibold">{t('account.preferences.title')}</h6>
          <p className="text-muted small mb-0 mt-1">{t('account.preferences.subtitle')}</p>
        </div>
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            {PREFS.map((pref) => (
              <div
                key={pref.key}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
              >
                <div>
                  <div className="small fw-medium">{t(pref.labelKey)}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t(pref.descKey)}
                  </div>
                </div>
                <div className="form-check form-switch mb-0 ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={prefs[pref.key]}
                    onChange={() => toggle(pref.key)}
                    style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 d-flex justify-content-end">
            <button
              className="btn btn-primary px-4"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? <span className="spinner-border spinner-border-sm me-2" />
                : null}
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesTab;
